import lidNameMap from './lidNameMap.js';

// WhatsApp group @mentions arrive as raw LID numbers (e.g. "@221693394047066").
// Replace each with the participant's name at render time (non-destructive — the
// stored content keeps the LID) using the Wali-sourced LID→{name,phone} map.
// Unknown LIDs are left as-is. Regenerate lidNameMap.js from the Wali export.
//
// linked=true renders a markdown link "[@Name](#wamention-<phone>)" so the thread
// can make mentions clickable (a click handler opens that number's chat); the plain
// form "@Name" is used for the conversation-list preview.
export const WA_MENTION_HREF_PREFIX = '#wamention-';

// Vue provide/inject key: MessageList provides the LIVE group roster index
// (digits -> participant) so bubbles resolve @<lid> from the real membership
// first and only fall back to the static Wali map. The static map went stale
// for Smith3D (15 staff LIDs mentioned in Aug 2026 were missing) — raw LID
// digits in a bubble mean nothing to staff (owner rule: name > phone > lid).
export const GROUP_ROSTER_INDEX = Symbol('groupRosterIndex');

// Roster row -> { name, phone, curated } for rendering. A roster "name" that is
// only the digits (member with neither name nor phone) is not a resolution.
// `curated` = the name came from the account's own contacts (controller flag),
// not the member's self-set WhatsApp push name.
export const rosterEntry = participant => {
  if (!participant) return null;
  const phone = String(participant.phone || '').replace(/\D/g, '');
  const name = String(participant.name || '').trim();
  const digits = String(participant.digits || '');
  if (name && name !== digits && name !== phone) {
    return { name, phone, curated: !!participant.curated };
  }
  if (phone) return { name: `+${phone}`, phone, curated: false };
  return null;
};

// Precedence (owner rule name > phone > lid): a curated contact name from the
// live roster, then the static Wali map (curated, may carry the phone a
// LID-only roster row lacks), then the roster push name. The phone is taken
// from whichever source has it.
export const resolveLidEntry = (lid, rosterIndex = null) => {
  const fromRoster =
    rosterIndex && typeof rosterIndex.get === 'function'
      ? rosterEntry(rosterIndex.get(lid))
      : null;
  const fromMap = lidNameMap[lid];
  if (fromRoster?.curated) {
    return { name: fromRoster.name, phone: fromRoster.phone || fromMap?.phone };
  }
  if (fromMap?.name) {
    return { name: fromMap.name, phone: fromMap.phone || fromRoster?.phone };
  }
  return fromRoster;
};

// Roster names are member-controlled WhatsApp push names; when the result is
// fed to the markdown renderer, a name like "Ali ]" or "[promo](https://…)"
// must not be able to break or forge markup.
const escapeMarkdown = value => value.replace(/([\\[\]()*_~`])/g, '\\$1');

export const resolveLidMentions = (
  text,
  { linked = false, rosterIndex = null, markdown = true } = {}
) =>
  (text || '').replace(/@(\d{8,})/g, (match, lid) => {
    const entry = resolveLidEntry(lid, rosterIndex);
    if (!entry || !entry.name) return match;
    const name = markdown ? escapeMarkdown(entry.name) : entry.name;
    if (linked && entry.phone) {
      return `[@${name}](${WA_MENTION_HREF_PREFIX}${entry.phone})`;
    }
    return `@${name}`;
  });

// Make Malaysian mobile numbers in message text clickable: +60196471560,
// 0196471560, 012-3456789, and the international "+60 19-647 1560" (a space/dash
// after the +60 country code). A separator after the prefix is allowed ONLY for the
// explicit "+60" form — a bare "60 1234567" (e.g. "RM60 1234567", or a "60 100 120"
// dimensions list) is not a phone number, so prices/quantities aren't mislinkified.
// A click opens that number's chat (same #wamention- handler as @mentions). The
// negative lookbehind avoids matching digits inside a URL or a longer number/order id.
// "!" is excluded too: staff wrap numbers as !012-3456789! to dodge marketplace
// detectors, and a leading "!" + the generated [x](href) link forms markdown IMAGE
// syntax ![x](href) — which blanks the whole bubble instead of showing the number.
const PHONE_RE = /(?<![\d/@.!-])((?:\+60[-\s]?|60|0)1\d(?:[-\s]?\d){7,8})/g;

const normalizePhoneForSearch = raw => {
  const digits = raw.replace(/\D/g, '');
  return digits.startsWith('0') ? `60${digits.slice(1)}` : digits;
};

export const linkifyPhoneNumbers = text =>
  (text || '').replace(
    PHONE_RE,
    match =>
      `[${match}](${WA_MENTION_HREF_PREFIX}${normalizePhoneForSearch(match)})`
  );

export default resolveLidMentions;
